import { PortalBaseService } from 'src/portal/portal-base.service';
import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { PortalProfileModel } from './portal-profile.models';
import { Operation } from 'fast-json-patch/module/core';
import { AdminPortalProfilePanelModel } from 'src/app/admin/admin-home/admin-portal/admin-portal-model';
import { map } from 'rxjs/operators';
import { Attribute } from 'src/app/shared/models/attributes/attribute';
import { StringIgnoreCaseComparator } from 'src/app/shared/utils/comparators';
import { BehaviorSubject, lastValueFrom, Observable, of } from 'rxjs';
import { AttributeWrite } from 'src/app/shared/models/attributes/attributeWrite';
import { ComplexAttributeWrite } from 'src/app/shared/models/attributes/complexAttributeWrite';
import { getParsedError } from 'src/app/shared/services/error-handler-factory.service';

@Injectable({
  providedIn: 'root'
})
export class PortalProfileService extends PortalBaseService {
  public readonly profile = new BehaviorSubject<PortalProfileModel>(null);
  public getProfile(): Promise<PortalProfileModel> {
    return lastValueFrom(
      this.get<PortalProfileModel>('portal/profile').pipe(
        map((data) => {
          // Ensure customFields is at least an empty array
          if (!data.customFields) {
            data.customFields = [];
          }
          data.customFields = data.customFields
            .map((attribute) => Attribute.parse(attribute))
            .sort(StringIgnoreCaseComparator('name'));
          this.profile.next(data);
          return data;
        })
      )
    );
  }

  public updateProfile(model: Operation[]): Promise<PortalProfileModel> {
    const body = JSON.stringify(model);
    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json-patch+json' })
    };
    return lastValueFrom(this.patch('portal/profile/system-fields', body, false, options));
  }

  public getProfilePanels(): Promise<Array<AdminPortalProfilePanelModel>> {
    return lastValueFrom(
      this.get<Array<AdminPortalProfilePanelModel>>('portal/definitions/profile-panels')
    );
  }

  public updateAttribute = async (
    attributeDefinitionId: string,
    attribute: AttributeWrite
  ): Promise<Attribute[]> => {
    try {
      const result = await lastValueFrom(
        this.post(
          `portal/profile/custom-fields/${attributeDefinitionId}`,
          JSON.stringify(attribute),
          null,
          true
        ).pipe(map(this.convertAttributesData))
      );
      if (result.length) {
        return result; //happy path
      }
      this.openSnackBar('unexpected response from updateAttribute', false);
    } catch (error) {
      const parsedMessage = getParsedError(error);

      const errorMessage =
        parsedMessage.isGenericError && error.message ? error.message : parsedMessage.message;

      this.openSnackBar(errorMessage, false);
    }
    return null; //error condition.
  };

  public addOrUpdateComplexAttribute(
    attributeDefinitionId: string,
    attributes: ComplexAttributeWrite[]
  ): Observable<Attribute[]> {
    let body = JSON.stringify(attributes);
    return this.post(
      `portal/profile/custom-fields/${attributeDefinitionId}/complex`,
      body,
      null
    ).pipe(map(this.convertAttributesData));
  }

  public deleteAttribute(attributeDefinitionId: string): Observable<any> {
    return this.delete(`portal/profile/custom-fields/${attributeDefinitionId}`, null, true);
  }

  public async deleteEmail(emailId: string): Promise<boolean> {
    try {
      await lastValueFrom(this.delete(`portal/profile/emails/${emailId}`, null, true));
      await this.getProfile();
      return true;
    } catch (error) {
      const parsedMessage = getParsedError(error);
      const errorMessage =
        parsedMessage.isGenericError && error.message ? error.message : parsedMessage.message;
      this.openSnackBar(errorMessage, false);
      return false;
    }
  }

  public async addEmail(email: string): Promise<{ success: boolean; emailId?: string }> {
    try {
      const result = (await lastValueFrom(
        this.post('portal/profile/emails', JSON.stringify(email), null, true)
      )) as { id: string } | undefined;
      await this.getProfile();
      return { success: true, emailId: result?.id };
    } catch (error) {
      const parsedMessage = getParsedError(error);
      const errorMessage =
        parsedMessage.isGenericError && error.message ? error.message : parsedMessage.message;
      this.openSnackBar(errorMessage, false);
      return { success: false };
    }
  }

  public async makePrimaryEmail(emailId: string): Promise<boolean> {
    try {
      await lastValueFrom(this.patch(`portal/profile/emails/${emailId}/make-primary`, null, true));
      await this.getProfile();
      return true;
    } catch (error) {
      const parsedMessage = getParsedError(error);
      const errorMessage =
        parsedMessage.isGenericError && error.message ? error.message : parsedMessage.message;
      this.openSnackBar(errorMessage, false);
      return false;
    }
  }

  public async verifyEmail(
    emailId: string,
    verificationCode: string
  ): Promise<{ success: boolean; errorMessage?: string }> {
    try {
      await lastValueFrom(
        this.patch(
          `portal/profile/emails/${emailId}/verify`,
          JSON.stringify(verificationCode),
          true
        )
      );
      await this.getProfile();
      return { success: true };
    } catch (error) {
      const parsedMessage = getParsedError(error);
      const errorMessage =
        parsedMessage.isGenericError && error.message ? error.message : parsedMessage.message;
      return { success: false, errorMessage };
    }
  }

  public async resendVerificationCode(emailId: string): Promise<boolean> {
    try {
      await lastValueFrom(
        this.post(`portal/profile/emails/${emailId}/send-verification`, null, null, true)
      );
      return true;
    } catch (error) {
      const parsedMessage = getParsedError(error);
      const errorMessage =
        parsedMessage.isGenericError && error.message ? error.message : parsedMessage.message;
      this.openSnackBar(errorMessage, false);
      return false;
    }
  }

  private convertAttributesData(data): Array<Attribute> {
    let attributes: Array<Attribute> = [];
    return data ? data.map((a) => Attribute.parse(a)) : attributes;
  }
}
